'use client';

/**
 * Auto-enables the user's `is_auto_verify_enabled` preference the first time
 * they load the app under the `AUTO_*` PostHog variant. Mounted at the app
 * root alongside `AutoVerifyCrossSessionWatcher` and gated by
 * `IS_HEIDI_VERIFY_AUTO_MODE_ENABLED()`, so by the time this renders we know
 * the user is in the AUTO_K cohort.
 *
 * Fires at most once per user: `auto_verify_preference_set` is a durable
 * server-side marker, set the first time the preference is established (here,
 * or by the settings `AutoVerifyToggle`). Once set we never auto-enable again,
 * so an explicit opt-out via the toggle is respected across reloads and
 * devices. The `fired` ref only guards a duplicate fire within a single mount
 * while the mutation is in flight; if the backend predates the marker field
 * (`auto_verify_preference_set` undefined) behaviour degrades to the prior
 * once-per-load auto-enable.
 */

import { useEffect, useRef } from 'react';

import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { useQueryUser } from '@/hooks/query/useQueryUser';

export const AutoVerifyAutoEnable = () => {
  const { data: user } = useQueryUser();
  const { mutate: updateUser } = useMutateUpdateUser();
  const fired = useRef(false);

  useEffect(() => {
    if (
      user &&
      !user.auto_verify_preference_set &&
      !user.is_auto_verify_enabled &&
      !fired.current
    ) {
      fired.current = true;
      updateUser({
        is_auto_verify_enabled: true,
        auto_verify_preference_set: true,
      });
    }
  }, [user, updateUser]);

  return null;
};
